import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Loader2 } from 'lucide-react';
import {
  BrandProposalForBusiness,
  acceptBrandProposal,
  declineBrandProposal,
  getCampaignProposals,
  getProposalPreviewTicket,
  markProposalViewed,
} from '../../api/payment/brand-deal.api';
import BrandWatermarkedMedia, { WatermarkOverlay } from './brand-watermarked-media';
import { BrandThreadToggle } from './brand-thread-panel';
import {
  PROPOSAL_LIMITS,
  PROPOSAL_ORDERER_NOTE,
  acceptConfirmText,
  proposalStatusLabel,
  validateDeclineReason,
} from '../../utils/brand-proposal';
import { displayReviewDays, draftWatermarkText } from '../../utils/brand-creative';

/**
 * Đề xuất của một chiến dịch "Nhận đề xuất" phía người đặt (spec 2026-10-12 §6): mở từng đề xuất mới thấy nội dung (mở là ghi "đã xem"),
 * chữ phủ dấu tên, ảnh/video chỉ là bản đóng dấu qua vé, chọn hoặc không chọn kèm lý do. File gốc chỉ dùng được sau khi chọn.
 */

const errorMessage = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

const formatDate = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// Khai ngoài component cha (landmine remount) — một file đề xuất xem qua vé bản đóng dấu.
const ProposalMedia: React.FC<{ proposalId: string; fileId: string; kind: 'image' | 'video' }> = ({ proposalId, fileId, kind }) => {
  const loadTicket = useCallback(() => getProposalPreviewTicket(proposalId, fileId), [proposalId, fileId]);
  return <BrandWatermarkedMedia kind={kind} loadTicket={loadTicket} />;
};

const ProposalCard: React.FC<{
  proposal: BrandProposalForBusiness;
  watermark: string;
  open: boolean;
  busy: boolean;
  onToggle: () => void;
  onAccept: () => void;
  onDecline: (reason: string) => Promise<boolean>;
}> = ({ proposal, watermark, open, busy, onToggle, onAccept, onDecline }) => {
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');
  const { creator } = proposal;
  const pending = proposal.status === 'Pending';
  const blockCopy = (e: React.SyntheticEvent) => e.preventDefault();

  const submitDecline = async () => {
    if (await onDecline(reason)) {
      setDeclining(false);
      setReason('');
    }
  };

  return (
    <div className="rounded-lg border ui-divider p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate font-medium ui-text">{creator.displayName}</div>
          <div className="truncate text-xs ui-muted">
            @{creator.userName} · {creator.followers.toLocaleString('vi-VN')} người theo dõi · {creator.completedDeals} deal đã hoàn thành
          </div>
        </div>
        <span className="ui-divider inline-flex shrink-0 items-center rounded-full border px-2.5 py-1 text-[12px] font-semibold ui-text">
          {proposalStatusLabel(proposal.status)}
        </span>
      </div>
      <div className="mt-1 text-[12px] ui-muted">Gửi lúc: {formatDate(proposal.createdAt)}</div>
      {proposal.declineReason && <p className="mt-2 text-sm text-amber-600 dark:text-amber-400">Lý do: {proposal.declineReason}</p>}
      {proposal.expireReason && <p className="mt-2 text-sm text-amber-600 dark:text-amber-400">{proposal.expireReason}</p>}

      {pending && (
        <button type="button" onClick={onToggle} className="mt-2 text-[13px] font-semibold text-[#0057FF]">
          {open ? 'Thu gọn' : 'Xem đề xuất'}
        </button>
      )}

      {pending && open && (
        <div className="mt-2 flex flex-col gap-3">
          {proposal.text && (
            <div
              className="relative select-none whitespace-pre-wrap rounded-lg bg-slate-50 p-3 text-sm ui-text dark:bg-slate-800/50"
              onCopy={blockCopy}
              onCut={blockCopy}
              onContextMenu={blockCopy}
            >
              {proposal.text}
              <WatermarkOverlay text={watermark} />
            </div>
          )}
          {proposal.files.length > 0 && (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {proposal.files.map((f) => (
                <ProposalMedia key={f.fileId} proposalId={proposal.id} fileId={f.fileId} kind={f.kind} />
              ))}
            </div>
          )}
          {declining ? (
            <div className="flex flex-col gap-2">
              <label className="text-sm ui-text">
                Lý do (không bắt buộc, tối đa 300 ký tự)
                <textarea
                  className="mt-1 w-full resize-none rounded-md border ui-divider bg-transparent px-3 py-2 text-sm outline-none"
                  rows={3}
                  value={reason}
                  maxLength={PROPOSAL_LIMITS.declineReasonMaxLength + 50}
                  onChange={(e) => setReason(e.target.value)}
                />
              </label>
              <div className="flex gap-2">
                <button type="button" disabled={busy} onClick={submitDecline} className="rounded-md bg-rose-600 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-60">
                  Gửi
                </button>
                <button type="button" disabled={busy} onClick={() => setDeclining(false)} className="rounded-md border ui-divider px-3 py-1.5 text-sm font-semibold ui-text">
                  Huỷ
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              <button type="button" disabled={busy} onClick={onAccept} className="rounded-md bg-[#0057FF] px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-60">
                {busy ? 'Đang xử lý…' : 'Chọn đề xuất'}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => setDeclining(true)}
                className="rounded-md border border-rose-200 px-3 py-1.5 text-sm font-semibold text-rose-600 disabled:opacity-60 dark:border-rose-900/40"
              >
                Không chọn
              </button>
            </div>
          )}
        </div>
      )}
      <div className="mt-3">
        <BrandThreadToggle threadId={proposal.threadId} />
      </div>
    </div>
  );
};

export interface BrandCampaignProposalsProps {
  campaignId: string;
  brandName: string;
  reviewDays?: number | null;
  /** Chọn/không chọn xong thì suất và số đề xuất chờ đổi — cha tải lại chi tiết và danh sách nhiệm vụ. */
  onChanged?: () => void;
}

const BrandCampaignProposals: React.FC<BrandCampaignProposalsProps> = ({ campaignId, brandName, reviewDays, onChanged }) => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<BrandProposalForBusiness[]>([]);
  const [openIds, setOpenIds] = useState<Set<string>>(new Set());
  const [busyId, setBusyId] = useState<string | null>(null);
  const viewedRef = useRef<Set<string>>(new Set());

  const load = useCallback(() => {
    setLoading(true);
    getCampaignProposals(campaignId)
      .then(setItems)
      .catch(() => toast.error('Không tải được danh sách đề xuất.'))
      .finally(() => setLoading(false));
  }, [campaignId]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = (id: string) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    if (!viewedRef.current.has(id)) {
      viewedRef.current.add(id);
      markProposalViewed(id).catch(() => undefined);
    }
  };

  const accept = async (p: BrandProposalForBusiness) => {
    if (!window.confirm(acceptConfirmText(displayReviewDays(reviewDays)))) return;
    setBusyId(p.id);
    try {
      await acceptBrandProposal(p.id);
      toast.success('Đã chọn đề xuất, bài đang chờ bạn duyệt.');
      onChanged?.();
    } catch (e) {
      toast.error(errorMessage(e, 'Không chọn được đề xuất.'));
    } finally {
      setBusyId(null);
      load();
    }
  };

  const decline = async (p: BrandProposalForBusiness, reason: string): Promise<boolean> => {
    const error = validateDeclineReason(reason);
    if (error) {
      toast.error(error);
      return false;
    }
    setBusyId(p.id);
    try {
      await declineBrandProposal(p.id, reason.trim() || undefined);
      toast.success('Đã báo creator đề xuất chưa được chọn.');
      onChanged?.();
      load();
      return true;
    } catch (e) {
      toast.error(errorMessage(e, 'Không gửi được.'));
      return false;
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="font-semibold ui-text">Đề xuất</div>
      <p className="text-xs ui-muted">{PROPOSAL_ORDERER_NOTE}</p>
      {loading ? (
        <div className="flex justify-center p-4">
          <Loader2 className="animate-spin ui-muted" />
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm ui-muted">Chưa có đề xuất nào.</p>
      ) : (
        items.map((p) => (
          <ProposalCard
            key={p.id}
            proposal={p}
            watermark={draftWatermarkText(brandName, p.threadId)}
            open={openIds.has(p.id)}
            busy={busyId === p.id}
            onToggle={() => toggle(p.id)}
            onAccept={() => accept(p)}
            onDecline={(reason) => decline(p, reason)}
          />
        ))
      )}
    </div>
  );
};

export default BrandCampaignProposals;
